import { useEffect, type RefObject } from "react";

export function useFaqAccordion(detailsRef: RefObject<HTMLDetailsElement | null>): void {
  useEffect(() => {
    const detail = detailsRef.current;
    if (!detail) return;
    const summary = detail.querySelector("summary");
    const body = detail.querySelector<HTMLElement>(".faq-body");
    if (!summary || !body) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let animation: Animation | null = null;

    const open = () => {
      detail.open = true;
      const target = body.scrollHeight;
      if (animation) animation.cancel();
      body.style.height = "0px";
      body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        animation = body.animate(
          [{ height: "0px" }, { height: target + "px" }],
          {
            duration: reduced ? 0 : 320,
            easing: "cubic-bezier(0.4, 0, 0.2, 1)",
          }
        );
        animation.onfinish = () => {
          body.style.height = "";
          body.style.overflow = "";
          animation = null;
        };
        animation.oncancel = () => {
          animation = null;
        };
      });
    };

    const close = () => {
      const start = body.offsetHeight;
      detail.classList.add("is-closing");
      if (animation) animation.cancel();
      body.style.overflow = "hidden";
      animation = body.animate(
        [{ height: start + "px" }, { height: "0px" }],
        {
          duration: reduced ? 0 : 280,
          easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        }
      );
      animation.onfinish = () => {
        detail.open = false;
        detail.classList.remove("is-closing");
        body.style.height = "";
        body.style.overflow = "";
        animation = null;
      };
      animation.oncancel = () => {
        animation = null;
      };
    };

    const onClick = (e: Event) => {
      e.preventDefault();
      if (reduced) {
        detail.open = !detail.open;
        return;
      }
      if (detail.open && !detail.classList.contains("is-closing")) {
        close();
      } else if (!detail.open) {
        open();
      }
    };

    summary.addEventListener("click", onClick);
    return () => {
      summary.removeEventListener("click", onClick);
      if (animation) animation.cancel();
    };
  }, [detailsRef]);
}
